import { addLocalDays, localDayKey, weekRange } from '../../../../Utils/dates/localDay';
import { formatDayMonth, formatShortDate } from '../../../../Utils/dates/formatDate';

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const WORD = { c: 'Completed', a: 'Assigned', d: 'Declined' };
const KEY = { Completed: 'c', Assigned: 'a', Declined: 'd' };
const km = metres => (typeof metres === 'number' && Number.isFinite(metres) ? metres : 0) / 1000;
const one = value => (Math.round(value * 10) / 10).toFixed(1);
// Completed > Assigned > Declined when a day holds several sessions.
const PRECEDENCE = ['c', 'a', 'd'];

const summarise = (cells, todayKey) => {
    const sessionDays = cells.filter(c => c.marker !== 'r');
    const done = cells.filter(c => c.marker === 'c');
    const open = sessionDays.filter(c => c.marker !== 'c');
    // "Assigned sessions" counts every workout in the window, whatever its status (declined included).
    const countOf = list => list.reduce((a, c) => a + c.count, 0);
    return {
        assigned: countOf(sessionDays),
        done: countOf(done),
        plannedKm: sessionDays.reduce((a, c) => a + c.plannedKm, 0),
        doneKm: done.reduce((a, c) => a + c.completedKm, 0),
        open
    };
};

/** Four local weeks (Monday first) ending with the current one, plus two 7-day comparisons. */
export const buildRhythm = (rows, now) => {
    const todayKey = localDayKey(now);
    const firstMonday = addLocalDays(weekRange(now).from, -21);

    const byDay = new Map();
    (rows || []).forEach(r => {
        const key = localDayKey(r.workoutStart);
        if (!byDay.has(key)) byDay.set(key, []);
        byDay.get(key).push(r);
    });

    const all = [];
    const weeks = Array.from({ length: 4 }, (_, w) => {
        const days = Array.from({ length: 7 }, (_, i) => {
            const date = addLocalDays(firstMonday, w * 7 + i);
            const key = localDayKey(date);
            const sessions = byDay.get(key) || [];
            const markers = sessions.map(s => KEY[s.status]).filter(Boolean);
            const marker = PRECEDENCE.find(m => markers.includes(m)) || 'r';
            const isToday = key === todayKey;
            const isPast = key < todayKey;
            const plannedKm = sessions.reduce((a, s) => a + km(s.volume), 0);
            const completedKm = sessions.filter(s => s.status === 'Completed').reduce((a, s) => a + km(s.volume), 0);
            const when = `${formatShortDate(date)}${isToday ? ' (today)' : ''}`;
            const what =
                marker === 'r'
                    ? 'Rest, no session'
                    : `${WORD[marker]}${marker === 'a' && isPast ? ', not marked completed' : ''} · ${one(plannedKm)} km`;
            const cell = {
                key, date, dow: DOW[i], marker, count: markers.length || 0, isToday, plannedKm, completedKm,
                label: `${when}: ${what}`
            };
            // a Completed-precedence day reports only its completed km, as the single marker is "done"
            if (marker === 'c') cell.label = `${when}: ${WORD.c} · ${one(plannedKm)} km`;
            all.push(cell);
            return cell;
        });
        const sum = summarise(days, todayKey);
        return {
            start: days[0].date,
            label: formatDayMonth(days[0].date),
            days,
            done: sum.done,
            assigned: sum.assigned,
            doneKm: Math.round(sum.doneKm * 10) / 10,
            isCurrent: days.some(d => d.key === todayKey)
        };
    });

    const counts = { c: 0, a: 0, d: 0, r: 0 };
    all.forEach(c => (counts[c.marker] += 1));

    const window = (fromIdx, toIdx) => {
        const cells = all.slice(fromIdx, toIdx + 1);
        return { cells, from: cells[0].date, to: cells[cells.length - 1].date, ...summarise(cells, todayKey) };
    };
    const ti = all.findIndex(c => c.key === todayKey);
    const cur = ti >= 6 ? window(ti - 6, ti) : null;
    const prev = ti >= 13 ? window(ti - 13, ti - 7) : null;

    const current = cur
        ? `${formatDayMonth(cur.from)} – ${formatDayMonth(cur.to)}: completed ${cur.done} of ${cur.assigned} assigned sessions, ${one(cur.doneKm)} of ${one(cur.plannedKm)} km${
              cur.open.length
                  ? ` (${cur.open.map(c => `${c.marker === 'a' ? 'assigned' : 'declined'} ${formatShortDate(c.date)}`).join(', ')})`
                  : ''
          }.`
        : '';
    const previous = prev
        ? `Previous 7 days, ${formatDayMonth(prev.from)} – ${formatDayMonth(prev.to)}: completed ${prev.done} of ${prev.assigned}, ${one(prev.doneKm)} of ${one(prev.plannedKm)} km.`
        : '';

    return { weeks, counts, current, previous };
};
